import moment from "moment-timezone";
import numeral from "numeral";
import { ConfidenceLevel, KeyResultBinaryChoice, KeyResultType } from "@teaming/common-enums";

const MAX_TITLE_LENGTH = 250;
const MAX_COMMENT_LENGTH = 512;

// eslint-disable-next-line no-unused-vars,max-statements
export const validateGoalForm = ( form, touches, i18n ) => {
	const { t, } = i18n.global;

	const errors = {
		title: "",
		assignedTeam: "",
		confidenceLevel: "",
		startDate: "",
		endDate: "",
		keyResultCount: "",
	};

	if ( touches.title ) {
		if ( !form.title.trim() ) {
			errors.title = t( "common-gokr.stores.validation.goalTitleMissing" );
		} else if ( form.title.length > MAX_TITLE_LENGTH ) {
			errors.title = t( "common-gokr.stores.validation.goalTitleLength", {
				length: MAX_TITLE_LENGTH,
			} );
		}
	}

	if ( touches.assignedTeamId && !form.assignedTeamId.trim() ) {
		errors.assignedTeamId = t( "common-gokr.stores.validation.goalAssignedTeamMissing" );
	}

	if ( touches.confidenceLevel ) {
		if ( !form.confidenceLevel.trim() ) {
			errors.confidenceLevel = t( "common-gokr.stores.validation.goalConfidenceLevelMissing" );
		}
		if ( !ConfidenceLevel.includes( form.confidenceLevel ) ) {
			errors.confidenceLevel = t( "common-gokr.stores.validation.goalConfidenceLevelInvalid" );
		}
	}

	if ( touches.startDate ) {
		const start = moment( form.startDate );
		if ( !start.isValid() ) {
			errors.startDate = t( "common-gokr.stores.validation.goalStartDateInvalid" );
		}
	}

	if ( touches.endDate ) {
		const end = moment( form.endDate );
		if ( !end.isValid() ) {
			errors.endDate = t( "common-gokr.stores.validation.goalEndDateInvalid" );
		}
		if ( form.startDate ) {
			const start = moment( form.startDate );
			if ( !end.isAfter( start ) ) {
				errors.endDate = t( "common-gokr.stores.validation.goalEndDateOrientation" );
			}
		}
	}

	if ( touches.keyResultCount ) {
		if ( form.keyResultCount === 0 ) {
			// NOTE: temporarily disabling KR count validation; we are allowing users to create goals without KRs for the time being
			// errors.keyResultCount = "One or more key results required.";
		}
	}

	if ( touches.comment ) {
		if ( form.comment.length > MAX_COMMENT_LENGTH ) {
			errors.comment = t( "common-gokr.stores.validation.goalCommentLength", {
				length: MAX_COMMENT_LENGTH,
			} );
		}
	}

	return errors;
};

const keyResultTypeValidators = {
	// eslint-disable-next-line no-unused-vars
	[ KeyResultType.BINARY ]( form, touches, i18n ) {
		const { t, } = i18n.global;

		const errors = {
			startValue: "",
			currentValue: "",
		};

		if ( touches.currentValue ) {
			if ( !KeyResultBinaryChoice.includes( form.currentValue ) ) {
				errors.startValue = t( "common-gokr.stores.validation.keyResultBinaryValueInvalid" );
			}
		}

		if ( form.targetValue !== KeyResultBinaryChoice.COMPLETE ) {
			errors.startValue = t( "common-gokr.stores.validation.keyResultBinaryValueInvalid" );
		}

		return errors;
	},
	// eslint-disable-next-line no-unused-vars
	[ KeyResultType.NUMERIC ]( form, touches, i18n ) {
		const { t, } = i18n.global;

		const errors = {
			startValue: "",
			currentValue: "",
		};

		if ( touches.currentValue ) {
			const currentValue = numeral( form.currentValue ).value();
			if ( currentValue === null ) {
				errors.currentValue = t( "common-gokr.stores.validation.keyResultNumericValueInvalid" );
			} else if ( currentValue < 0 ) {
				errors.currentValue = t( "common-gokr.stores.validation.keyResultNumericValueGTEQ0" );
			}
		}

		if ( touches.targetValue ) {
			const targetValue = numeral( form.targetValue ).value();
			if ( targetValue === null ) {
				errors.targetValue = t( "common-gokr.stores.validation.keyResultNumericValueInvalid" );
			} else if ( targetValue <= 0 ) {
				errors.targetValue = t( "common-gokr.stores.validation.keyResultNumericValueGT0" );
			}
		}

		return errors;
	},
	// eslint-disable-next-line no-unused-vars
	[ KeyResultType.CURRENCY ]( form, touches, i18n ) {
		return keyResultTypeValidators[ KeyResultType.NUMERIC ]( form, touches, i18n );
	},
	// eslint-disable-next-line no-unused-vars
	[ KeyResultType.PERCENTAGE ]( form, touches, i18n ) {
		const { t, } = i18n.global;

		const errors = keyResultTypeValidators[ KeyResultType.NUMERIC ]( form, touches, i18n );

		if ( !errors.targetValue ) {
			const targetValue = numeral( form.currentValue ).value();
			if ( targetValue > 100 ) {
				errors.targetValue = t( "common-gokr.stores.validation.keyResultNumericValueLTEQ100" );
			}
		}

		return errors;
	},
};

// eslint-disable-next-line no-unused-vars
export const validateKeyResultForm = ( form, touches, i18n ) => {
	const { t, } = i18n.global;

	const errors = {
		name: "",
		confidenceLevel: "",
		type: "",
		startValue: "",
		currentValue: "",
		targetValue: "",
		ownerId: "",
	};

	if ( touches.name && !form.name.trim() ) {
		errors.name = t( "common-gokr.stores.validation.keyResultNameMissing" );
	}

	if ( touches.confidenceLevel ) {
		if ( !ConfidenceLevel.includes( form.confidenceLevel ) ) {
			errors.confidenceLevel = t( "common-gokr.stores.validation.keyResultConfidenceLevelInvalid" );
		}
	}

	if ( touches.ownerId ) {
		if ( !form.ownerId ) {
			errors.ownerId = t( "common-gokr.stores.validation.keyResultOwnerMissing" );
		}
	}

	if ( touches.comment ) {
		if ( form.comment.length > MAX_COMMENT_LENGTH ) {
			errors.comment = t( "common-gokr.stores.validation.keyResultCommentLength", {
				length: MAX_COMMENT_LENGTH,
			} );
		}
	}

	if ( touches.type ) {
		if ( !KeyResultType.includes( form.type ) ) {
			errors.type = t( "common-gokr.stores.validation.keyResultTypeInvalid" );
		}
	}

	if ( errors.type ) {
		return errors;
	}

	const typeValidator = keyResultTypeValidators[ form.type ];

	return {
		...errors,
		...typeValidator( form, touches, i18n ),
	};
};


